export const STORE_SAVE_PROMPT_TEST_START_PARAMETER =
  "store_save_prompt_test";
export const STORE_SAVE_PROMPT_SHOW_COOLDOWN_MS =
  7 * 24 * 60 * 60 * 1_000;
export const STORE_SAVE_PROMPT_DISMISS_COOLDOWN_MS =
  14 * 24 * 60 * 60 * 1_000;
export const STORE_SAVE_PROMPT_TREATMENT_PERCENT = 50;
export const STORE_SAVE_PROMPT_SAVED_STORE_LIMIT = 3;

export type StoreSavePromptVariant = "off" | "control" | "treatment";

export function normalizeStoreSavePromptNextEligibleAt(
  value: unknown,
): number {
  return typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value > 0
    ? value
    : 0;
}

export function parseStoreSavePromptNextEligibleAt(
  value: string | null,
): number {
  if (!value) {
    return 0;
  }
  try {
    return normalizeStoreSavePromptNextEligibleAt(JSON.parse(value));
  } catch {
    return 0;
  }
}

export function storeSavePromptGlobalCooldownIsActive(
  nextEligibleAt: number,
  now = Date.now(),
): boolean {
  return normalizeStoreSavePromptNextEligibleAt(nextEligibleAt) > now;
}

export function extendStoreSavePromptCooldown(
  nextEligibleAt: number,
  durationMs: number,
  now = Date.now(),
): number {
  const current = normalizeStoreSavePromptNextEligibleAt(nextEligibleAt);
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    return current;
  }
  return Math.max(current, Math.trunc(now + durationMs));
}

function stableBucket(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return (hash >>> 0) % 100;
}

export function storeSavePromptVariant(options: {
  enabled: boolean;
  userId: string | null;
  startParameter: string | null;
  localPreview: boolean;
}): StoreSavePromptVariant {
  if (
    options.startParameter === STORE_SAVE_PROMPT_TEST_START_PARAMETER ||
    options.localPreview
  ) {
    return "treatment";
  }
  if (!options.enabled || !options.userId) {
    return "off";
  }
  return stableBucket(`kiloprice-store-save-v1:${options.userId}`) <
    STORE_SAVE_PROMPT_TREATMENT_PERCENT
    ? "treatment"
    : "control";
}

export function catalogPagesNeededForStorePrompt(pageCount: number): number {
  if (!Number.isFinite(pageCount) || pageCount < 2) {
    return Number.POSITIVE_INFINITY;
  }
  return Math.min(3, Math.max(2, Math.trunc(pageCount)));
}

export function catalogBrowsingQualifiesForStorePrompt(
  pageCount: number,
  visitedPages: ReadonlySet<number>,
): boolean {
  return visitedPages.size >= catalogPagesNeededForStorePrompt(pageCount);
}

export function savedStoreCountAllowsStorePrompt(
  savedStores: readonly string[],
): boolean {
  return new Set(savedStores.map((store) => store.trim()).filter(Boolean)).size <
    STORE_SAVE_PROMPT_SAVED_STORE_LIMIT;
}

export function parseStorePromptDismissals(
  value: string | null,
  now = Date.now(),
): Record<string, number> {
  if (!value) {
    return {};
  }
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const result: Record<string, number> = {};
    for (const [store, expiresAt] of Object.entries(parsed)) {
      const cleanedStore = store.trim();
      if (
        !cleanedStore ||
        cleanedStore.length > 80 ||
        typeof expiresAt !== "number" ||
        !Number.isFinite(expiresAt) ||
        expiresAt <= now
      ) {
        continue;
      }
      result[cleanedStore] = expiresAt;
    }
    return result;
  } catch {
    return {};
  }
}

export function storePromptIsCoolingDown(
  dismissals: Readonly<Record<string, number>>,
  store: string,
  now = Date.now(),
): boolean {
  return (dismissals[store] ?? 0) > now;
}

export function dismissStorePrompt(
  dismissals: Readonly<Record<string, number>>,
  store: string,
  now = Date.now(),
): Record<string, number> {
  return {
    ...dismissals,
    [store]: now + STORE_SAVE_PROMPT_DISMISS_COOLDOWN_MS,
  };
}
